/**
 * App Detail Component - Displays app information
 */

import { useEffect, useState } from 'preact/hooks';
import { db, doc, onSnapshot, AppData } from '../globals/firebase';
import { useLocation } from 'wouter-preact';
import { Globe, FileText, FileEdit, Settings, Terminal } from 'lucide-preact';
import { FeaturedAppCard } from './FeaturedAppCard';

interface AppDetailProps {
  appId: string;
}

export function AppDetail({ appId }: AppDetailProps) {
  const [, setLocation] = useLocation();

  const [app, setApp] = useState<AppData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Set up real-time listener for app data
  useEffect(() => {
    if (!appId) return;

    // Parse canonical appId: username@appname
    const parts = appId.split('@');
    if (parts.length !== 2) {
      setError('Invalid app ID format');
      setLoading(false);
      return;
    }

    const [username, appName] = parts;
    setLoading(true);
    setError(null);

    // Subscribe to app document with onSnapshot for real-time updates
    const appDocRef = doc(db, 'developers', username, 'apps', appName);
    const unsubscribe = onSnapshot(
      appDocRef,
      (snapshot) => {
        if (snapshot.exists()) {
          setApp({ id: appId, ...snapshot.data() } as AppData);
        } else {
          setError('App not found');
        }
        setLoading(false);
      },
      (err) => {
        console.error('Error loading app:', err);
        setError('Failed to load app');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [appId]);

  if (loading) {
    return (
      <div class="p-6">
        <div class="skeleton h-48 rounded-lg" />
      </div>
    );
  }

  if (error || !app) {
    return (
      <div class="p-6 text-center">
        <h2 class="text-xl font-semibold text-white mb-4">{error || 'App not found'}</h2>
        <button
          onClick={() => setLocation('/')}
          class="px-4 py-2 bg-neutral-700 hover:bg-neutral-600 rounded-lg text-white transition-colors"
        >
          Back to Store
        </button>
      </div>
    );
  }

  return (
    <div>
      <section class="featured-app mt-6 px-4">
        <FeaturedAppCard app={app} hoverBorder={false} />
      </section>
      <div class="app-detail-body p-6 space-y-4">
        <section>
          <h2 class="text-sm font-medium tracking-wide text-neutral-500 mb-4">Description</h2>
          <p class="text-neutral-300 leading-relaxed bg-neutral-800/50 rounded-2xl p-5">{app.description}</p>
        </section>
        <section>
          <h2 class="text-sm font-medium tracking-wide text-neutral-500 mb-4">Permissions</h2>
          <div class="permissions-list space-y-0 bg-neutral-800/50 rounded-2xl children:px-4">
            {app.permissions.net?.length > 0 && (
              <div class="permission-item flex gap-4 py-4 border-b border-neutral-700/60 last:border-0">
                <Globe class="w-6 h-6 text-neutral-400 flex-shrink-0 mt-0.5" />
                <div class="flex-1">
                  <strong class="text-white text-sm font-medium block mb-1">Network Access</strong>
                  <p class="text-neutral-400 text-xs">{app.permissions.net.join(', ')}</p>
                </div>
              </div>
            )}
            {app.permissions.read?.length > 0 && (
              <div class="permission-item flex gap-4 py-4 border-b border-neutral-700/60 last:border-0">
                <FileText class="w-6 h-6 text-neutral-400 flex-shrink-0 mt-0.5" />
                <div class="flex-1">
                  <strong class="text-white text-sm font-medium block mb-1">Read Files</strong>
                  <p class="text-neutral-400 text-xs">{app.permissions.read.join(', ')}</p>
                </div>
              </div>
            )}
            {app.permissions.write?.length > 0 && (
              <div class="permission-item flex gap-4 py-4 border-b border-neutral-700/60 last:border-0">
                <FileEdit class="w-6 h-6 text-neutral-400 flex-shrink-0 mt-0.5" />
                <div class="flex-1">
                  <strong class="text-white text-sm font-medium block mb-1">Write Files</strong>
                  <p class="text-neutral-400 text-xs">{app.permissions.write.join(', ')}</p>
                </div>
              </div>
            )}
            {app.permissions.env?.length > 0 && (
              <div class="permission-item flex gap-4 py-4 border-b border-neutral-700/60 last:border-0">
                <Settings class="w-6 h-6 text-neutral-400 flex-shrink-0 mt-0.5" />
                <div class="flex-1">
                  <strong class="text-white text-sm font-medium block mb-1">Environment Variables</strong>
                  <p class="text-neutral-400 text-xs">{app.permissions.env.join(', ')}</p>
                </div>
              </div>
            )}
            {app.permissions.run?.length > 0 && (
              <div class="permission-item flex gap-4 py-4 border-b border-neutral-700/60 last:border-0">
                <Terminal class="w-6 h-6 text-neutral-400 flex-shrink-0 mt-0.5" />
                <div class="flex-1">
                  <strong class="text-white text-sm font-medium block mb-1">Run Commands</strong>
                  <p class="text-neutral-400 text-xs">{app.permissions.run.join(', ')}</p>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
